import { useEffect, useState } from 'react';
import * as rtiService from '../../../services/rightToInformationCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type RightToInformationLookupScope = 'step1' | 'step2' | 'step4' | 'review';

type LookupBundle = {
  urgencyLevels: LookupItem[];
  preferredMethodOfAccess: LookupItem[];
  preferredLanguages: LookupItem[];
};

const EMPTY: LookupBundle = {
  urgencyLevels: [],
  preferredMethodOfAccess: [],
  preferredLanguages: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useRightToInformationLookups(scope: RightToInformationLookupScope) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step4');

  useEffect(() => {
    if (scope === 'step4') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step1') {
        const [access, langs] = await Promise.all([
          rtiService.getPreferredMethodOfAccess(),
          rtiService.getPreferredLanguages(),
        ]);
        return {
          preferredMethodOfAccess: access.success ? access.data : [],
          preferredLanguages: langs.success ? langs.data : [],
        };
      }
      if (scope === 'step2') {
        const urg = await rtiService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }
      const [urg, access, langs] = await Promise.all([
        rtiService.getUrgencyLevels(),
        rtiService.getPreferredMethodOfAccess(),
        rtiService.getPreferredLanguages(),
      ]);
      return {
        urgencyLevels: urg.success ? urg.data : [],
        preferredMethodOfAccess: access.success ? access.data : [],
        preferredLanguages: langs.success ? langs.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  return {
    loading,
    urgencyLevels: data.urgencyLevels,
    preferredMethodOfAccess: data.preferredMethodOfAccess,
    preferredLanguages: data.preferredLanguages,
  };
}
